import api from '../lib/api';

export interface Forecast {
  id: string;
  name: string;
  organizationId: string;
  forecastType: 'CONTACT_VOLUME' | 'STAFFING';
  startDate: string;
  endDate: string;
  interval: number;
  modelType: 'HISTORICAL_AVERAGE' | 'ERLANG_C' | 'LINEAR_REGRESSION' | 'SEASONAL_DECOMP';
  modelVersion: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED' | 'PUBLISHED';
  modelParameters: Record<string, any>;
  accuracyScore?: number;
  mape?: number;
  mae?: number;
  lastAccuracyCheck?: string;
  linkedScheduleId?: string;
  skillGroupId?: string;
  createdAt: string;
  updatedAt: string;
  dataPoints?: ForecastDataPoint[];
}

export interface ForecastDataPoint {
  id: string;
  forecastId: string;
  timestamp: string;
  predictedValue: number;
  upperBound?: number;
  lowerBound?: number;
  actualValue?: number;
  variance?: number;
  dataSource?: string;
  integrationId?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateForecastDto {
  name: string;
  organizationId: string;
  startDate?: string;
  endDate?: string;
  forecastWeeks?: number;
  historicalWeeks?: number;
  interval?: number;
  modelType: 'HISTORICAL_AVERAGE' | 'ERLANG_C' | 'LINEAR_REGRESSION' | 'SEASONAL_DECOMP';
  modelParameters?: {
    historicalWeeks?: number;
    weightDecay?: number;
    confidenceLevel?: number;
    aht?: number;
    targetServiceLevel?: number;
    targetAnswerTime?: number;
    shrinkage?: number;
  };
  channels?: string[];
  skillGroupId?: string;
}

export interface UpdateModelParametersDto {
  parameters: Record<string, any>;
  regenerate: boolean;
}

export interface SLAImpactParams {
  scheduleId?: string;
  targetServiceLevel?: number;
  targetAnswerTime?: number;
}

export interface SLAImpactResult {
  forecastId: string;
  scheduleId: string | null;
  summary: {
    totalIntervals: number;
    atRiskIntervals: number;
    meetingIntervals: number;
    averageGap: number;
    averagePredictedSL: number;
    targetSL: number;
  };
  intervals: Array<{
    timestamp: string;
    requiredAgents: number;
    scheduledAgents: number;
    gap: number;
    targetSL: number;
    predictedSL: number;
    slImpact: number;
    status: 'MEETING' | 'AT_RISK';
  }>;
}

export interface GenerateScheduleParams {
  shiftTemplate?: {
    duration: number;
    breakDuration: number;
    breakCount: number;
    lunchDuration: number;
  };
  preferredShiftTimes?: string[];
  maxHoursPerAgent?: number;
  publishImmediately?: boolean;
  erlangCParams?: {
    aht?: number;
    targetServiceLevel?: number;
    targetAnswerTime?: number;
    shrinkage?: number;
  };
}

export interface AccuracyMetrics {
  forecastId: string;
  mape: number;
  mae: number;
  accuracyScore: number;
  dataPointsEvaluated: number;
}

export interface DailyAccuracyEntry {
  date: string;
  mape: number;
  mae: number;
  predictedVolume: number;
  actualVolume: number;
  dataPointCount: number;
}

export interface DailyAccuracyResult {
  daily: DailyAccuracyEntry[];
  overall: {
    mape: number;
    mae: number;
    accuracyScore: number;
  };
}

// Forecast Assumptions types
export type FactorType =
  | 'BASE_VOLUME'
  | 'GROWTH'
  | 'EFFICIENCY'
  | 'EVENT_OUTAGE'
  | 'EVENT_PROMOTION'
  | 'EVENT_LAUNCH'
  | 'EVENT_OTHER';

export interface ForecastAssumption {
  id: string;
  forecastId: string;
  factorType: FactorType;
  name: string;
  description?: string;
  percentageImpact?: number;
  absoluteImpact?: number;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  createdBy?: string;
}

export interface CreateAssumptionDto {
  factorType: FactorType;
  name: string;
  description?: string;
  percentageImpact?: number;
  absoluteImpact?: number;
  startDate?: string;
  endDate?: string;
}

export interface UpdateAssumptionDto {
  name?: string;
  description?: string;
  percentageImpact?: number;
  absoluteImpact?: number;
  startDate?: string;
  endDate?: string;
}

export interface FactorBreakdownItem {
  id: string;
  factorType: FactorType;
  name: string;
  percentageImpact: number | null;
  absoluteImpact: number | null;
  volumeContribution: number;
  cumulativeVolume: number;
}

export interface FactorBreakdown {
  baseVolume: number;
  factors: FactorBreakdownItem[];
  finalVolume: number;
}

// Shift Optimization types
export type ChannelType = 'PHONE' | 'CHAT' | 'EMAIL' | 'SMS' | 'SOCIAL';
export type AgentGroupingStrategy = 'MAXIMIZE' | 'BALANCED' | 'NONE';

/**
 * Shift pattern defines how working days are grouped together
 * e.g., "5x8" = 5 days per week, 8 hours per day
 *       "4x10" = 4 days per week, 10 hours per day
 */
export interface ShiftPattern {
  /** Pattern name for display, e.g., "5x8", "4x10", "Custom" */
  name: string;
  /** Number of days worked per week */
  daysPerWeek: number;
  /** Hours worked per day */
  hoursPerDay: number;
  /** Specific days of week (0=Sunday, 6=Saturday). If not specified, will be auto-assigned based on demand */
  workingDays?: number[];
}

/** Predefined common shift patterns */
export const SHIFT_PATTERNS: Record<string, ShiftPattern> = {
  /** Standard 5 days × 8 hours (Mon-Fri) */
  STANDARD_5X8: { name: '5x8', daysPerWeek: 5, hoursPerDay: 8, workingDays: [1, 2, 3, 4, 5] },
  /** Compressed 4 days × 10 hours (Mon-Thu) */
  COMPRESSED_4X10: { name: '4x10', daysPerWeek: 4, hoursPerDay: 10, workingDays: [1, 2, 3, 4] },
  /** Compressed 4 days × 10 hours (Tue-Fri) */
  COMPRESSED_4X10_TUE_FRI: { name: '4x10', daysPerWeek: 4, hoursPerDay: 10, workingDays: [2, 3, 4, 5] },
  /** Weekend coverage (Sat-Sun + 3 weekdays) */
  WEEKEND_COVERAGE: { name: '5x8', daysPerWeek: 5, hoursPerDay: 8, workingDays: [0, 1, 2, 5, 6] },
};

export interface ShiftOptimizationConfig {
  targetOccupancy?: number;
  maxOccupancyVariance?: number;
  preferScheduleContinuity?: boolean;
  priorForecastId?: string;
  agentGroupingStrategy?: AgentGroupingStrategy;
  channels?: ChannelType[];
  daysOfWeek?: number[];
  shiftDuration?: number;
  coverageBuffer?: number;
  minAgentsPerShift?: number;
  maxShiftsPerDay?: number;
  startDate?: string;
  endDate?: string;
  /** Shift pattern configuration - groups working days together */
  shiftPattern?: ShiftPattern;
  /** Generate multiple pattern variants for comparison */
  generatePatternVariants?: boolean;
}

export interface IntervalCoverage {
  interval: number;
  time: string;
  demandRequired: number;
  coverageProvided: number;
  occupancy: number;
}

export interface OptimizedShiftTemplate {
  id: string;
  startTime: string;
  duration: number;
  agentsNeeded: number;
  occupancyScore: number;
  channel?: ChannelType;
  intervalCoverage: IntervalCoverage[];
  isRecommended: boolean;
  continuityScore?: number;
  /** Working days for this shift (0=Sunday, 6=Saturday) */
  workingDays: number[];
  /** Pattern name for display, e.g., "5x8", "4x10" */
  patternName: string;
  /** Total weekly hours (daysPerWeek × hoursPerDay) */
  weeklyHours: number;
}

export interface PriorShiftPattern {
  startTime: string;
  duration: number;
  channel?: ChannelType;
  frequency: number;
  agentCount: number;
}

export interface ChannelBalance {
  channel: ChannelType;
  forecastPercentage: number;   // % of total forecast demand
  plannedPercentage: number;    // % of planned staffing
  variance: number;             // Difference (positive = overstaffed)
  status: 'balanced' | 'understaffed' | 'overstaffed';
}

export interface OptimizationRuleViolation {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  shiftTime?: string;
  reason: string;
  severity: 'warning' | 'error';
  impact: string;
}

export interface ScheduleContinuitySummary {
  preservedPatterns: number;     // How many shifts match prior patterns
  changedPatterns: number;       // How many shifts are new/different
  preservationPercentage: number;
  agentImpact: {
    noChange: number;            // Agents with same schedule
    minorChange: number;         // Agents with ±1 hour change
    majorChange: number;         // Agents with >1 hour change
  };
}

export interface ShiftOptimizationResult {
  shifts: OptimizedShiftTemplate[];
  summary: {
    /** Total unique agents needed (workforce size) */
    totalAgents: number;
    /** Peak concurrent agents at any interval */
    peakConcurrentAgents: number;
    /** Sum of agents across all shifts (for reference, not workforce size) */
    totalShiftAgents: number;
    averageOccupancy: number;
    coveragePercentage: number;
    continuityScore: number;
    staffingEfficiency: number;  // Single unified metric (75-90% is optimal)
  };
  /** The shift pattern used for generation */
  pattern: ShiftPattern;
  channelBalance: ChannelBalance[];
  gapAnalysis: {
    uncoveredIntervals: number;
    overstaffedIntervals: number;
    recommendations: string[];
  };
  ruleViolations: OptimizationRuleViolation[];
  scheduleContinuity: ScheduleContinuitySummary;
  priorPatterns?: PriorShiftPattern[];
  /** Alternative pattern options for comparison (if generatePatternVariants was true) */
  patternVariants?: Array<{
    pattern: ShiftPattern;
    totalAgents: number;
    coveragePercentage: number;
    averageOccupancy: number;
  }>;
}

export interface AutoGenerateShiftsOptions {
  shiftDuration?: number;
  shiftStartTimes?: string[];
  shifts?: Array<{ startTime: string; duration: number; agentsNeeded: number }>;
  minAgentsPerShift?: number;
  coverageBuffer?: number;
  startDate?: string;
  endDate?: string;
  daysOfWeek?: number[];
  teamId?: string;
  channel?: ChannelType;
  createdBy?: string;
  enforceRules?: boolean;
}

export interface AutoGenerateShiftsResult {
  created: number;
  shifts: PlannedShift[];
  violations: RuleViolation[];
  skippedDueToRules: number;
}

export interface PlannedShift {
  id: string;
  forecastId: string;
  name: string;
  startTime: string;
  endTime: string;
  requiredCount: number;
  assignedCount: number;
  status: 'PLANNED' | 'APPROVED' | 'SCHEDULED' | 'CANCELLED';
  shiftType: string;
  notes?: string;
  skillGroupId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RuleViolation {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  severity: 'ERROR' | 'WARNING' | 'INFO';
  message: string;
  context?: Record<string, any>;
}

export interface PlanningSummary {
  forecastId: string;
  totalShifts: number;
  totalAgents: number;
  coverageStats: {
    coveredIntervals: number;
    totalIntervals: number;
    coveragePercentage: number;
  };
  occupancyStats: {
    averageOccupancy: number;
    peakOccupancy: number;
    minOccupancy: number;
  };
}

export enum OutlierReason {
  HOLIDAY = 'HOLIDAY',
  SYSTEM_OUTAGE = 'SYSTEM_OUTAGE',
  SPECIAL_EVENT = 'SPECIAL_EVENT',
  DATA_ERROR = 'DATA_ERROR',
  OTHER = 'OTHER',
}

export interface MarkOutlierDto {
  startDate: string;
  endDate: string;
  reason: OutlierReason;
  notes?: string;
  excludeFromModel: boolean;
}

// Historical CSV import types
export type CsvType = 'HISTORICAL_ACTUALS' | 'INTERVAL_PREDICTIONS';

export interface CsvDetectionResult {
  type: CsvType;
  columns: string[];
  channelColumn?: string;
  dateColumn: string;
  valueColumn: string;
}

export interface HistoricalImportOptions {
  intervalMinutes?: number;
  replaceExisting?: boolean;
}

export interface HistoricalImportResult {
  imported: number;
  channels: string[];
  dateRange: { start: string; end: string };
  message: string;
}

export interface ImportJobResponse {
  jobId: string;
  message: string;
}

export interface ImportJobStatus {
  jobId: string;
  forecastId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  result?: HistoricalImportResult;
  error?: string;
  startedAt: string;
  completedAt?: string;
}

export interface RegenerationJobResult {
  dataPointsGenerated: number;
  startDate: string;
  endDate: string;
}

export interface RegenerationJobStatus {
  jobId: string;
  forecastId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  result?: RegenerationJobResult;
  error?: string;
  startedAt: string;
  completedAt?: string;
}

interface RegenerationJobResponse {
  jobId: string;
  message: string;
}

export enum DateRangeEditType {
  SET_VALUE = 'SET_VALUE',
  ADJUST_BY_PERCENTAGE = 'ADJUST_BY_PERCENTAGE',
  ADJUST_BY_AMOUNT = 'ADJUST_BY_AMOUNT',
  MULTIPLY = 'MULTIPLY',
}

export interface ForecastValueEdit {
  timestamp: string;
  newValue: number;
  reason?: string;
}

export interface DateRangeEdit {
  startDate: string;
  endDate: string;
  editType: DateRangeEditType;
  value: number;
  reason?: string;
}

export interface EditForecastValuesDto {
  edits?: ForecastValueEdit[];
  dateRangeEdits?: DateRangeEdit[];
  applyToFutureDays?: boolean;
}

export enum SmoothingMethod {
  MOVING_AVERAGE = 'MOVING_AVERAGE',
  EXPONENTIAL = 'EXPONENTIAL',
  SAVITZKY_GOLAY = 'SAVITZKY_GOLAY',
}

export interface SmoothForecastDto {
  method: SmoothingMethod;
  windowSize?: number;
  alpha?: number;
  startDate?: string;
  endDate?: string;
}

export interface ModelComparison {
  modelType: string;
  accuracy: number;
  mape: number;
  mae: number;
  confidence: number;
  description: string;
  isRecommended: boolean;
}

class ForecastsService {
  async getAll(organizationId?: string): Promise<Forecast[]> {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/forecasts', { params });
    return response.data;
  }

  async getById(id: string): Promise<Forecast> {
    const response = await api.get(`/forecasts/${id}`);
    return response.data;
  }

  async getDataPoints(
    id: string,
    filters?: { channel?: string; team?: string; shift?: string }
  ): Promise<ForecastDataPoint[]> {
    const params = new URLSearchParams();

    if (filters?.channel) {
      params.append('channel', filters.channel);
    }
    if (filters?.team) {
      params.append('team', filters.team);
    }
    if (filters?.shift) {
      params.append('shift', filters.shift);
    }

    const queryString = params.toString();
    const url = `/forecasts/${id}/data-points${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data;
  }

  async generate(dto: GenerateForecastDto): Promise<Forecast> {
    const response = await api.post('/forecasts/generate', dto);
    return response.data;
  }

  async compareModels(dto: GenerateForecastDto): Promise<{ comparisons: ModelComparison[]; recommended: string }> {
    const response = await api.post('/forecasts/compare-models', dto);
    return response.data;
  }

  async updateModelParameters(id: string, dto: UpdateModelParametersDto): Promise<Forecast> {
    const response = await api.patch(`/forecasts/${id}/model-parameters`, dto);
    return response.data;
  }

  /**
   * Start an async regeneration job.
   * Use this for large datasets that would timeout with synchronous updateModelParameters.
   */
  async startRegenerationJob(
    id: string,
    dto: UpdateModelParametersDto,
  ): Promise<RegenerationJobResponse> {
    const response = await api.post<RegenerationJobResponse>(
      `/forecasts/${id}/regenerate-async`,
      dto,
      { timeout: 30000 }, // 30 seconds to start the job
    );
    return response.data;
  }

  /**
   * Get the status of an async regeneration job
   */
  async getRegenerationJobStatus(jobId: string): Promise<RegenerationJobStatus> {
    const response = await api.get<RegenerationJobStatus>(
      `/forecasts/regeneration-status/${jobId}`,
    );
    return response.data;
  }

  /**
   * Regenerate forecast asynchronously with polling for completion.
   * This starts an async regeneration job and polls for completion.
   * Returns when the regeneration is complete or fails.
   */
  async regenerateAsync(
    id: string,
    dto: UpdateModelParametersDto,
    onProgress?: (status: RegenerationJobStatus) => void,
  ): Promise<RegenerationJobResult> {
    // Start the async regeneration job
    const { jobId } = await this.startRegenerationJob(id, dto);

    // Poll for completion
    const pollInterval = 2000; // 2 seconds
    const maxPolls = 300; // 10 minutes max (300 * 2s)
    let polls = 0;

    while (polls < maxPolls) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      polls++;

      const status = await this.getRegenerationJobStatus(jobId);

      if (onProgress) {
        onProgress(status);
      }

      if (status.status === 'completed') {
        if (!status.result) {
          throw new Error('Regeneration completed but no result returned');
        }
        return status.result;
      }

      if (status.status === 'failed') {
        throw new Error(status.error || 'Regeneration failed');
      }
    }

    throw new Error('Regeneration timed out after 10 minutes');
  }

  async syncActuals(id: string): Promise<{ message: string; accuracy: AccuracyMetrics }> {
    const response = await api.post(`/forecasts/${id}/sync-actuals`);
    return response.data;
  }

  async getAccuracy(id: string): Promise<AccuracyMetrics> {
    const response = await api.get(`/forecasts/${id}/accuracy`);
    return response.data;
  }

  async getDailyAccuracy(id: string): Promise<DailyAccuracyResult> {
    const response = await api.get(`/forecasts/${id}/accuracy/daily`);
    return response.data;
  }

  async getSLAImpact(id: string, params: SLAImpactParams): Promise<SLAImpactResult> {
    const response = await api.get(`/forecasts/${id}/sla-impact`, { params });
    return response.data;
  }

  async generateSchedule(id: string, params: GenerateScheduleParams): Promise<any> {
    const response = await api.post(`/forecasts/${id}/generate-schedule`, params);
    return response.data;
  }

  async exportCSV(id: string): Promise<Blob> {
    const response = await api.get(`/forecasts/${id}/export-csv`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importCSV(id: string, file: File): Promise<{ imported: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/forecasts/${id}/import-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Detect the type of CSV based on headers.
   * - HISTORICAL_ACTUALS: Daily data with Date, Channel, Volume columns
   * - INTERVAL_PREDICTIONS: Interval data with Timestamp, Predicted Value columns
   */
  detectCsvType(headers: string[]): CsvDetectionResult {
    const headersLower = headers.map((h) => h.toLowerCase().trim());

    // Check for historical actuals format: Date, Channel, Historical Volume (or Volume, Actual)
    const hasChannel = headersLower.includes('channel');
    const hasVolumeColumn = headersLower.some(
      (h) =>
        h.includes('historical volume') ||
        h.includes('volume') ||
        h.includes('actual') ||
        h.includes('contacts') ||
        h.includes('calls'),
    );
    const hasDateColumn = headersLower.some(
      (h) => h === 'date' || h.includes('date'),
    );
    const hasTimestamp = headersLower.some(
      (h) => h === 'timestamp' || h.includes('time'),
    );

    // Historical format: date + channel + volume, without explicit timestamp
    if (hasChannel && hasVolumeColumn && hasDateColumn && !hasTimestamp) {
      const channelIdx = headersLower.indexOf('channel');
      const dateIdx = headersLower.findIndex((h) => h === 'date' || h.includes('date'));
      const valueIdx = headersLower.findIndex(
        (h) =>
          h.includes('historical volume') ||
          h.includes('volume') ||
          h.includes('actual') ||
          h.includes('contacts') ||
          h.includes('calls'),
      );

      return {
        type: 'HISTORICAL_ACTUALS',
        columns: headers,
        channelColumn: headers[channelIdx],
        dateColumn: headers[dateIdx],
        valueColumn: headers[valueIdx],
      };
    }

    // Default: interval predictions format
    const timestampIdx = headersLower.findIndex(
      (h) => h === 'timestamp' || h === 'date' || h === 'time',
    );
    const predictedIdx = headersLower.findIndex(
      (h) =>
        h.includes('predicted') ||
        h.includes('value') ||
        h.includes('volume') ||
        h.includes('forecast'),
    );

    return {
      type: 'INTERVAL_PREDICTIONS',
      columns: headers,
      dateColumn: headers[timestampIdx] || headers[0],
      valueColumn: headers[predictedIdx] || headers[1],
    };
  }

  /**
   * Import historical volume data from CSV (daily format).
   * This starts an async import job and polls for completion.
   * Returns when the import is complete or fails.
   */
  async importHistoricalCSV(
    id: string,
    file: File,
    options: HistoricalImportOptions = {},
    onProgress?: (status: ImportJobStatus) => void,
  ): Promise<HistoricalImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options.intervalMinutes) {
      formData.append('intervalMinutes', options.intervalMinutes.toString());
    }
    if (options.replaceExisting !== undefined) {
      formData.append('replaceExisting', options.replaceExisting.toString());
    }

    // Start the async import job
    const startResponse = await api.post<ImportJobResponse>(
      `/forecasts/${id}/import-historical-csv`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000, // 30 seconds to start the job
      },
    );

    const { jobId } = startResponse.data;

    // Poll for completion
    const pollInterval = 2000; // 2 seconds
    const maxPolls = 300; // 10 minutes max (300 * 2s)
    let polls = 0;

    while (polls < maxPolls) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      polls++;

      const status = await this.getImportJobStatus(jobId);

      if (onProgress) {
        onProgress(status);
      }

      if (status.status === 'completed') {
        if (!status.result) {
          throw new Error('Import completed but no result returned');
        }
        return status.result;
      }

      if (status.status === 'failed') {
        throw new Error(status.error || 'Import failed');
      }
    }

    throw new Error('Import timed out after 10 minutes');
  }

  /**
   * Get the status of an async import job
   */
  async getImportJobStatus(jobId: string): Promise<ImportJobStatus> {
    const response = await api.get<ImportJobStatus>(`/forecasts/import-status/${jobId}`);
    return response.data;
  }

  async markOutliers(id: string, dto: MarkOutlierDto): Promise<any> {
    const response = await api.post(`/forecasts/${id}/outliers`, dto);
    return response.data;
  }

  async removeOutliers(id: string, timestamps: string[]): Promise<any> {
    const response = await api.delete(`/forecasts/${id}/outliers`, {
      data: { timestamps },
    });
    return response.data;
  }

  async editForecastValues(id: string, dto: EditForecastValuesDto): Promise<any> {
    const response = await api.patch(`/forecasts/${id}/edit-values`, dto);
    return response.data;
  }

  async smoothForecast(id: string, dto: SmoothForecastDto): Promise<any> {
    const response = await api.post(`/forecasts/${id}/smooth`, dto);
    return response.data;
  }

  async publish(id: string, force: boolean = false): Promise<{ forecast: Forecast; message: string; validationWarnings?: string[] }> {
    const params = force ? { force: 'true' } : {};
    const response = await api.post(`/forecasts/${id}/publish`, {}, { params });
    return response.data;
  }

  async unpublish(id: string): Promise<Forecast> {
    const response = await api.post(`/forecasts/${id}/unpublish`);
    return response.data;
  }

  async activate(id: string): Promise<Forecast> {
    const response = await api.post(`/forecasts/${id}/activate`);
    return response.data;
  }

  async archive(id: string): Promise<Forecast> {
    const response = await api.post(`/forecasts/${id}/archive`);
    return response.data;
  }

  async editPublishedForecast(id: string, dto: EditForecastValuesDto): Promise<any> {
    const response = await api.patch(`/forecasts/${id}/edit-published`, dto);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/forecasts/${id}`);
  }

  // Forecast Assumptions methods
  async getAssumptions(forecastId: string): Promise<ForecastAssumption[]> {
    const response = await api.get(`/forecasts/${forecastId}/assumptions`);
    return response.data;
  }

  async createAssumption(forecastId: string, dto: CreateAssumptionDto): Promise<ForecastAssumption> {
    const response = await api.post(`/forecasts/${forecastId}/assumptions`, dto);
    return response.data;
  }

  async updateAssumption(forecastId: string, assumptionId: string, dto: UpdateAssumptionDto): Promise<ForecastAssumption> {
    const response = await api.put(`/forecasts/${forecastId}/assumptions/${assumptionId}`, dto);
    return response.data;
  }

  async deleteAssumption(forecastId: string, assumptionId: string): Promise<void> {
    await api.delete(`/forecasts/${forecastId}/assumptions/${assumptionId}`);
  }

  async getFactorBreakdown(forecastId: string): Promise<FactorBreakdown> {
    const response = await api.get(`/forecasts/${forecastId}/assumptions/factor-breakdown`);
    return response.data;
  }

  // Shift Optimization methods
  async optimizeShifts(forecastId: string, config: ShiftOptimizationConfig): Promise<ShiftOptimizationResult> {
    const response = await api.post(`/forecasts/${forecastId}/shift-planning/optimize`, config);
    return response.data;
  }

  async getPriorShiftPatterns(forecastId: string, priorForecastId?: string): Promise<PriorShiftPattern[]> {
    const params = priorForecastId ? { priorForecastId } : {};
    const response = await api.get(`/forecasts/${forecastId}/shift-planning/prior-patterns`, { params });
    return response.data;
  }

  async autoGenerateShifts(forecastId: string, options: AutoGenerateShiftsOptions): Promise<AutoGenerateShiftsResult> {
    const response = await api.post(`/forecasts/${forecastId}/shift-planning/shifts/auto-generate`, options);
    return response.data;
  }

  async getPlannedShifts(forecastId: string, options?: { includeAllocations?: boolean }): Promise<PlannedShift[]> {
    const params = options?.includeAllocations ? { includeAllocations: 'true' } : {};
    const response = await api.get(`/forecasts/${forecastId}/shift-planning/shifts`, { params });
    return response.data;
  }

  async getPlanningSummary(forecastId: string): Promise<PlanningSummary> {
    const response = await api.get(`/forecasts/${forecastId}/shift-planning/planning-summary`);
    return response.data;
  }
}

export const forecastsService = new ForecastsService();
